import type { ISocketProvider, WebSocketLike } from "./types";
import { socketService } from "../services/socketService";
import { STORAGE_KEYS } from "@config/app.config";
import { getLocalStorage } from "@common";
class WebSocketWrapper implements WebSocketLike {
  get connected(): boolean {
    return socketService.isConnected();
  }

  get disconnected(): boolean {
    return !socketService.isConnected();
  }

  connect(): void {
    const token = getLocalStorage(STORAGE_KEYS.AUTH_TOKEN);
    if (token) {
      socketService.connect(token).catch((error) => {
        console.error("WebSocketWrapper: Connection error", error);
      });
    }
  }

  disconnect(): void {
    socketService.disconnect();
  }

  on(event: string, handler: (...args: unknown[]) => void): void {
    void event;
    void handler;
    console.warn(
      "WebSocketWrapper.on() is not used directly. Use socketService.subscribe() instead."
    );
  }

  off(event: string, handler?: (...args: unknown[]) => void): void {
    void event;
    void handler;
    console.warn(
      "WebSocketWrapper.off() is not used directly. Use socketService.unsubscribe() instead."
    );
  }

  emit(event: string, data?: unknown): void {
    socketService.send({
      type: event,
      data: data,
    });
  }

  once(event: string, handler: (...args: unknown[]) => void): void {
    void event;
    void handler;
    console.warn("WebSocketWrapper.once() is not implemented.");
  }
}

export class StandaloneSocketProvider implements ISocketProvider {
  private connectionListeners: Set<(isConnected: boolean) => void> = new Set();
  private connectionStateUnsubscribe: (() => void) | null = null;
  private wsWrapper: WebSocketWrapper = new WebSocketWrapper();
  private socketUrl: string | null = null;

  constructor(socketUrl?: string | null) {
    this.socketUrl = socketUrl || null;
    if (this.socketUrl) {
      socketService.setSocketUrl(this.socketUrl);
    }
  }

  connect(token?: string): void {
    socketService.connect(token).catch((error) => {
      console.error("StandaloneSocketProvider: Connection error", error);
    });

    if (!this.connectionStateUnsubscribe) {
      this.connectionStateUnsubscribe = socketService.onConnectionStateChange(
        (state) => {
          const isConnected = state === "connected";
          this.notifyConnectionChange(isConnected);
        }
      );
    }
  }

  disconnect(): void {
    socketService.disconnect();
    if (this.connectionStateUnsubscribe) {
      this.connectionStateUnsubscribe();
      this.connectionStateUnsubscribe = null;
    }
    this.notifyConnectionChange(false);
  }

  getSocket(): WebSocketLike | null {
    return this.wsWrapper;
  }

  isConnected(): boolean {
    return socketService.isConnected();
  }

  emit(event: string, data?: unknown): void {
    socketService.send({
      type: event,
      data: data,
    });
  }

  on(event: string, handler: (...args: unknown[]) => void): void {
    void event;
    void handler;
    console.warn(
      "StandaloneSocketProvider.on() is not used directly. Events are handled via socketService."
    );
  }

  off(event: string, handler?: (...args: unknown[]) => void): void {
    void event;
    void handler;
    console.warn("StandaloneSocketProvider.off() is not used directly.");
  }

  onConnectionChange(callback: (isConnected: boolean) => void): () => void {
    this.connectionListeners.add(callback);

    return () => {
      this.connectionListeners.delete(callback);
    };
  }

  private notifyConnectionChange(isConnected: boolean): void {
    this.connectionListeners.forEach((listener) => listener(isConnected));
  }
}
